import { extractApiError } from '@/shared/utils/handle-api-error'

export type MissingAttachment = {
  id: string
  name: string
  description?: string
}

/** El código con el que el servidor rechaza un avance por documentos faltantes. */
export const MISSING_ATTACHMENTS_CODE = 'NEGOTIATION_MISSING_REQUIRED_ATTACHMENTS'

/**
 * Los documentos que faltan, cuando el fallo de avanzar es ESE. `null` si es cualquier otro.
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
 *
 * El servidor no rechaza el avance con un código a secas: manda **la lista de qué falta**, y su
 * docblock dice para qué — «la pantalla abre un diálogo con lo que falta en vez de un toast que no
 * dice nada». Los dos lugares que intentaban leerla lo hacían a mano, y **los dos leían el sobre
 * equivocado**:
 *
 * | Dónde | Buscaba el código en | Buscaba la lista en |
 * | --- | --- | --- |
 * | El botón del expediente | `data.message`, contra `'ERROR_…'` con prefijo | `data.missingAttachments` |
 * | Las acciones del panel | `data.errorCode` | `data.details.missingAttachments` |
 * | **Lo que v2 manda de verdad** | **`data.code`**, sin prefijo | **`data.errors.missingAttachments`** |
 *
 * v2 responde `application/problem+json` (RFC 7807): el código viaja en `code` y el contenido en
 * `errors`. Así que el diálogo **no se abría desde ninguno de los dos**, y en su lugar salía un
 * toast genérico — con la lista de documentos llegando por la red y tirándose a la basura.
 *
 * Lo llamativo es que la traducción ya estaba escrita: `extractApiError` normaliza las TRES formas
 * en que esta API reporta errores —el filtro global, Problem Details y el legacy de Nest— a un
 * `{ errorCode, details }` único. Las dos pantallas la esquivaron y se armaron la suya. Es la
 * tercera vez en esta corrida que el problema no es que falte la pieza compartida sino que hay una
 * copia a mano al lado.
 *
 * Por eso esto devuelve **la lista o `null`** en vez de exponer el código y el payload por
 * separado: quien lo use no puede acertarle a la mitad.
 */
export function missingAttachmentsFromError(error: unknown): MissingAttachment[] | null {
  const apiError = extractApiError(error)
  if (apiError?.errorCode !== MISSING_ATTACHMENTS_CODE) return null

  const missing = (apiError.details as { missingAttachments?: unknown } | undefined)
    ?.missingAttachments

  // El código sin la lista sigue siendo ese rechazo: se abre el diálogo vacío, que dice «faltan
  // adjuntos» sin enumerarlos, en vez de caer al toast genérico y perder de qué se trataba.
  if (!Array.isArray(missing)) return []

  return missing.filter(
    (item): item is MissingAttachment =>
      Boolean(item) && typeof (item as MissingAttachment).name === 'string'
  )
}
